/**
 * `/picker?for=relay`: choose files for a Relay message, in a popup.
 *
 * Relay's web client opens this and gets back one share link per file. The
 * vault stays here: Relay receives what its Qt client posts, the link fields,
 * and never a key. Everything about who receives that is in `../picker.ts`.
 *
 * Signing in cannot happen in this window. mauvely.com isolates its pages from
 * their opener, so the moment this popup navigated there it would lose Relay
 * for good. Signed out, it sends the person to sign in in a tab of their own
 * and waits; the vault unlocks in place, as it does everywhere else.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, errorText, type NodeWire } from '../api';
import { useApp } from '../state';
import { ds } from '../ds';
import { useT } from '../i18n';
import { createShareLink } from '../crypto/share';
import {
  decryptNode,
  ensureFolderPath,
  keyForNode,
  listFolder,
  uploadFile,
  type DecryptedNode,
  type FolderContents
} from '../nodes';
import { dragHasFiles, readDrop } from '../dnd';
import { display, fileIcon, formatBytes, mono, monoPlain, RADIUS } from '../theme';
import { SkeletonLines } from '../components/Skeleton';
import { accountOrigin, Unreachable, VaultSetup, VaultUnlock } from './Vault';
import {
  MAX_PICKER_FILES,
  nameFits,
  numberedName,
  pickerGateHere,
  pickerResult,
  RELAY_FOLDER,
  type PickedFile
} from '../picker';

export function Picker() {
  const t = useT();
  const { status } = useApp();
  // Read once: the opener is the window that asked, and nothing later in this
  // page's life can make a missing one appear.
  const gate = useMemo(pickerGateHere, []);

  if (!gate.ok) {
    return (
      <Frame>
        <p style={{ ...lead, margin: 0 }}>{t('picker.opens-from-relay')}</p>
      </Frame>
    );
  }

  if (status === 'loading') {
    return (
      <Frame>
        <SkeletonLines lines={3} label={t('picker.loading')} />
      </Frame>
    );
  }
  if (status === 'signedOut') return <SignInElsewhere path="/signin" title={t('picker.sign-in-first')} />;
  if (status === 'secondFactor') {
    return <SignInElsewhere path="/signin/two-factor" title={t('picker.one-more-step')} />;
  }
  if (status === 'unreachable') return <Unreachable />;
  if (status === 'noVault') return <VaultSetup />;
  if (status === 'locked') return <VaultUnlock />;

  return <Choose target={gate.target} />;
}

// ── the frame ───────────────────────────────────────────────────────────────

const lead: React.CSSProperties = {
  fontSize: 15,
  lineHeight: 1.45,
  color: 'var(--text-body)'
};

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        background: 'var(--app-ground)',
        padding: 12,
        display: 'flex'
      }}
    >
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: 'var(--app-panel)',
          borderRadius: 16,
          padding: '22px 22px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Sign in in a new tab, then come back. See the note at the top. */
function SignInElsewhere({ path, title }: { path: string; title: string }) {
  const t = useT();
  const { Button } = ds();
  const { retry } = useApp();

  return (
    <Frame>
      <div style={mono}>{t('picker.share-to-relay')}</div>
      <h1 style={display(24)}>{title}</h1>
      <p style={{ ...lead, margin: 0 }}>{t('picker.sign-in-in-a-new-tab')}</p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          onClick={() => window.open(`${accountOrigin()}${path}`, '_blank', 'noopener')}
        >
          {t('picker.sign-in')}
        </Button>
        <Button variant="secondary" onClick={retry}>
          {t('picker.try-again')}
        </Button>
      </div>
    </Frame>
  );
}

// ── choosing ────────────────────────────────────────────────────────────────

type Item =
  | { key: string; kind: 'device'; file: File }
  | { key: string; kind: 'cloud'; node: DecryptedNode };

let nextKey = 0;

function Choose({ target }: { target: string }) {
  const t = useT();
  const { Button, Icon } = ds();
  const { me } = useApp();

  const [tab, setTab] = useState<'device' | 'cloud'>('device');
  const [items, setItems] = useState<Item[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // What a failed confirm already finished, so trying again neither uploads a
  // file twice nor mints a second link for it.
  const uploaded = useRef(new Map<string, string>());
  const shared = useRef(new Map<string, PickedFile>());

  const room = MAX_PICKER_FILES - items.length;

  const addFiles = (files: File[]) => {
    const limit = me?.maxFileBytes ?? null;
    const fits = limit === null ? files : files.filter((f) => f.size <= limit);
    const added = fits.slice(0, Math.max(0, room));
    if (fits.length < files.length) setNotice(t('picker.too-large', { limit: formatBytes(limit) }));
    else if (added.length < fits.length) setNotice(t('picker.at-most', { count: MAX_PICKER_FILES }));
    else setNotice(null);
    setItems((prev) => [
      ...prev,
      ...added.map((file) => ({ key: `d${nextKey++}`, kind: 'device' as const, file }))
    ]);
  };

  const toggleNode = (node: DecryptedNode) => {
    const existing = items.find((i) => i.kind === 'cloud' && i.node.id === node.id);
    if (existing) {
      setItems((prev) => prev.filter((i) => i !== existing));
      return;
    }
    if (room <= 0) {
      setNotice(t('picker.at-most', { count: MAX_PICKER_FILES }));
      return;
    }
    setNotice(null);
    setItems((prev) => [...prev, { key: `c${node.id}`, kind: 'cloud', node }]);
  };

  const share = async () => {
    if (!me?.rootNodeId || items.length === 0) return;
    // Before anything uploads: every link made here is live, and one that
    // cannot be delivered is left behind.
    if (!items.every((i) => nameFits(i.kind === 'device' ? i.file.name : i.node.name))) {
      setNotice(t('picker.name-too-long'));
      return;
    }
    if (!openerHere()) {
      setNotice(t('picker.relay-was-closed'));
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      let folder: { parent: NodeWire; chain: NodeWire[] } | null = null;
      const out: PickedFile[] = [];
      for (const [index, item] of items.entries()) {
        setProgress(t('picker.sharing', { n: index + 1, total: items.length }));
        let picked = shared.current.get(item.key);
        if (!picked) {
          let nodeId = item.kind === 'cloud' ? item.node.id : uploaded.current.get(item.key);
          if (!nodeId && item.kind === 'device') {
            folder ??= await relayFolder(me.rootNodeId);
            nodeId = await uploadInto(folder, item.file);
            uploaded.current.set(item.key, nodeId);
          }
          picked = await shareNode(nodeId!);
          shared.current.set(item.key, picked);
        }
        out.push(picked);
      }

      const opener = openerHere();
      if (!opener) {
        setNotice(t('picker.relay-was-closed'));
        return;
      }
      // The fixed origin, never '*': a Relay tab that has since navigated
      // somewhere else receives nothing.
      opener.postMessage(pickerResult(out), target);
      window.close();
    } catch (err) {
      setNotice(errorText(err, t('picker.could-not-share')));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Frame>
      <div style={mono}>{t('picker.share-to-relay')}</div>
      <h1 style={display(24)}>{t('picker.choose-files')}</h1>

      <div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" variant={tab === 'device' ? 'soft' : 'ghost'} onClick={() => setTab('device')}>
          {t('picker.this-device')}
        </Button>
        <Button size="sm" variant={tab === 'cloud' ? 'soft' : 'ghost'} onClick={() => setTab('cloud')}>
          {t('picker.your-cloud')}
        </Button>
      </div>

      {tab === 'device' ? (
        <div
          onDragOver={(e) => {
            if (!dragHasFiles(e.dataTransfer)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            if (busy || !dragHasFiles(e.dataTransfer)) return;
            // Read inside the handler: a DataTransfer is empty once it returns.
            // A dropped folder arrives flattened; Relay attaches files.
            void readDrop(e.dataTransfer).then((dropped) => addFiles(dropped.map((d) => d.file)));
          }}
          style={{
            border: `1.5px dashed ${dragging ? 'var(--primary)' : 'var(--border-strong)'}`,
            background: dragging ? 'var(--primary-soft)' : 'transparent',
            borderRadius: RADIUS.card,
            padding: '22px 16px',
            display: 'grid',
            justifyItems: 'center',
            gap: 10,
            textAlign: 'center'
          }}
        >
          <Icon name="upload-cloud" size={22} color="var(--text-muted)" />
          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{t('picker.drop-files-here')}</span>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => fileInput.current?.click()}>
            {t('picker.choose-from-this-device')}
          </Button>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) addFiles(Array.from(e.target.files));
              e.target.value = '';
            }}
          />
        </div>
      ) : (
        <CloudBrowser
          rootId={me?.rootNodeId ?? null}
          chosen={new Set(items.flatMap((i) => (i.kind === 'cloud' ? [i.node.id] : [])))}
          onToggle={busy ? () => {} : toggleNode}
        />
      )}

      {items.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          <div style={mono}>{t('picker.to-share', { count: items.length })}</div>
          {items.map((item) => {
            const name = item.kind === 'device' ? item.file.name : item.node.name;
            const bytes = item.kind === 'device' ? item.file.size : item.node.plainSizeBytes;
            return (
              <div key={item.key} style={row}>
                <span style={{ ...truncate, flex: 1, minWidth: 0, fontSize: 14 }} title={name}>
                  {name}
                </span>
                <span style={monoPlain}>{formatBytes(bytes)}</span>
                <button
                  type="button"
                  aria-label={t('picker.remove', { name })}
                  disabled={busy}
                  onClick={() => setItems((prev) => prev.filter((i) => i !== item))}
                  style={plainButton}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {notice && <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>{notice}</span>}

      <div style={{ marginTop: 'auto', display: 'grid', gap: 10 }}>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{t('picker.view-only-links')}</span>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
          {progress && <span style={{ ...monoPlain, marginRight: 'auto' }}>{progress}</span>}
          <Button variant="ghost" onClick={() => window.close()}>
            {t('picker.cancel')}
          </Button>
          <Button variant="primary" disabled={busy || items.length === 0} onClick={() => void share()}>
            {t('picker.share-to-relay')}
          </Button>
        </div>
      </div>
    </Frame>
  );
}

const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 4px',
  borderBottom: '1px solid var(--border-subtle)'
};

const truncate: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const plainButton: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  padding: 4,
  display: 'grid',
  placeItems: 'center'
};

/** The person's own files, a folder at a time. */
function CloudBrowser({
  rootId,
  chosen,
  onToggle
}: {
  rootId: string | null;
  chosen: Set<string>;
  onToggle: (node: DecryptedNode) => void;
}) {
  const t = useT();
  const { Icon } = ds();
  const [folderId, setFolderId] = useState(rootId);
  const [contents, setContents] = useState<FolderContents | null>(null);
  const [failed, setFailed] = useState<unknown>(null);

  useEffect(() => {
    if (!folderId) return;
    let live = true;
    setContents(null);
    setFailed(null);
    listFolder(folderId)
      .then((c) => live && setContents(c))
      .catch((err: unknown) => live && setFailed(err ?? new Error()));
    return () => {
      live = false;
    };
  }, [folderId]);

  if (failed) {
    return (
      <span style={{ fontSize: 13.5, color: 'var(--danger-600)' }}>
        {errorText(failed, t('picker.could-not-open-folder'))}
      </span>
    );
  }
  if (!contents) return <SkeletonLines lines={4} label={t('picker.loading')} />;

  const atRoot = contents.parent.id === rootId;
  const shown = contents.nodes.filter((n) => n.kind === 'folder' || n.hasContent);

  return (
    <div style={{ display: 'grid', gap: 2, maxHeight: 260, overflowY: 'auto' }}>
      {!atRoot && (
        <button
          type="button"
          style={{ ...row, ...plainButton, display: 'flex', justifyContent: 'flex-start' }}
          onClick={() => setFolderId(contents.parent.parentId)}
        >
          <Icon name="arrow-left" size={14} />
          <span style={{ ...truncate, fontSize: 14, color: 'var(--text-strong)' }}>
            {contents.parent.name}
          </span>
        </button>
      )}
      {shown.length === 0 && <span style={{ ...monoPlain, padding: 6 }}>{t('picker.nothing-here')}</span>}
      {shown.map((node) => {
        const on = chosen.has(node.id);
        return (
          <button
            key={node.id}
            type="button"
            aria-pressed={node.kind === 'file' ? on : undefined}
            onClick={() => (node.kind === 'folder' ? setFolderId(node.id) : onToggle(node))}
            style={{
              ...row,
              border: 'none',
              borderRadius: RADIUS.control,
              background: on ? 'var(--primary-soft)' : 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
              color: 'var(--text-strong)'
            }}
          >
            <Icon name={fileIcon(node.kind, node.mime)} size={15} color="var(--text-muted)" />
            <span style={{ ...truncate, flex: 1, minWidth: 0, fontSize: 14 }}>
              {node.name}
            </span>
            {node.kind === 'folder' ? (
              <Icon name="chevron-right" size={14} color="var(--text-faint)" />
            ) : on ? (
              <Icon name="check" size={14} color="var(--primary)" />
            ) : (
              <span style={monoPlain}>{formatBytes(node.plainSizeBytes)}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── the work ────────────────────────────────────────────────────────────────

/** The Relay folder at the top of the person's Cloud, made if missing. */
async function relayFolder(rootId: string) {
  const { node, ancestors } = await api.nodes.get(rootId);
  return ensureFolderPath(node, ancestors, [RELAY_FOLDER], new Map());
}

/**
 * Upload into the Relay folder, numbering the name when it is taken.
 *
 * Never over the existing file: that one may already be linked from another
 * conversation, and replacing it would change what that link opens.
 */
async function uploadInto(folder: { parent: NodeWire; chain: NodeWire[] }, file: File): Promise<string> {
  for (let n = 1; ; n++) {
    const named = n === 1 ? file : new File([file], numberedName(file.name, n), { type: file.type });
    try {
      return (await uploadFile(named, folder.parent, folder.chain)).id;
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'duplicate_name') || n >= 50) throw err;
    }
  }
}

function openerHere(): Window | null {
  const opener = window.opener as Window | null;
  return opener && !opener.closed ? opener : null;
}

/**
 * A view-only link with no password and no expiry, the link Relay's Qt client
 * makes. The node key is wrapped to the link secret here, as `ShareDialog`
 * does; only the secret and the ids leave this page.
 */
async function shareNode(nodeId: string): Promise<PickedFile> {
  const { node, ancestors } = await api.nodes.get(nodeId);
  const nodeKey = await keyForNode(node, ancestors);
  const plain = await decryptNode(node, nodeKey);
  const link = await createShareLink(nodeKey);
  const { id } = await api.links.create(node.id, {
    token: link.token,
    nodeKeyWrapped: link.nodeKeyWrapped,
    role: 'viewer',
    passwordSalt: null,
    passwordVerifier: null
  });
  return {
    nodeId: node.id,
    revisionId: node.currentRevisionId ?? '',
    linkId: id,
    secret: link.fragment,
    name: plain.name,
    mime: plain.mime ?? 'application/octet-stream',
    bytes: plain.plainSizeBytes
  };
}
